import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  Ban,
  CalendarDays,
  Mail,
  MessageSquare,
  Send,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Certification } from "../../../../../models/certification";
import {
  getStatusLabelKey,
  isTerminalStatus,
} from "../../../../../models/certificationStatus";
import { AxiosHttpClient } from "../../../../../services/AxiosHttpService";
import { useAuth } from "../../../../Reusable/AuthProvider";
import { ScrollToBottomContainer } from "../../../../Reusable/ScrollToBottomContainer";
import {
  getUserFullName,
  getUserInitials,
  imageIsDefault,
  useDateFormatting,
} from "../../../../Reusable/Utils";
import { useWebSocket } from "../../../../Reusable/useWebSocket";
import CertificationConversation from "../CertificationConversation";
import { getStatusVisual, StatusBadge } from "../certificationUi";

export default function CertificationDetails() {
  const { certificationId } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatDateTime } = useDateFormatting();
  const [certification, setCertification] = useState<Certification | null>(
    null
  );
  const [inputMessage, setInputMessage] = useState("");

  const getCertification = () => {
    AxiosHttpClient.get<Certification>(
      "api/v1/docuveritas/conversations/messages/certification/" +
        certificationId
    )
      .then(setCertification)
      .catch(console.error);
  };

  const addMessageToCertification = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputMessage.trim()) return;
    AxiosHttpClient.post(
      "api/v1/docuveritas/conversations/messages/certifications/message",
      {
        content: inputMessage,
        certificationId: certificationId,
      }
    )
      .then(() => {
        setInputMessage("");
        getCertification();
      })
      .catch((err) => {});
  };

  const cancelCertification = () => {
    AxiosHttpClient.post(
      "api/v1/docuveritas/conversations/messages/certifications/" +
        certificationId +
        "/cancel",
      {}
    )
      .then(() => getCertification())
      .catch((err) => {});
  };

  const { subscribe } = useWebSocket();

  useEffect(() => {
    if (!certification) return;

    // ces subscribe sont appelés via pendingSubscriptions si pas encore connecté
    const subCertMsgs = subscribe(
      `/topic/${certification.id}/certificationMessages`,
      (msg) => {
        const n = JSON.parse(msg.body) as Notification;
        getCertification();
      }
    );

    return () => {
      subCertMsgs.unsubscribe();
    };
  }, [certification, subscribe]);

  useEffect(() => {
    getCertification();
  }, [certificationId]);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back header */}
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/feature/certifications")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-semibold tracking-tight">
            {t("CERTIFICATIONS_DETAILS_BACK_LABEL")}
          </h1>
        </div>
        {certification &&
          user?.id === certification.requestedBy?.id &&
          !isTerminalStatus(certification.status) && (
            <Button
              variant="outline"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={cancelCertification}
            >
              <Ban className="h-4 w-4" />
              {t("CERTIFICATIONS_DETAILS_CANCEL")}
            </Button>
          )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column: context + conversation */}
        <div className="space-y-6 lg:col-span-2">
          {/* Question context */}
          <Card>
            <CardContent className="space-y-4 p-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="font-medium">
                  {certification?.category}
                </Badge>
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {formatDateTime(certification?.requestedAt)}
                </span>
              </div>

              <h2 className="text-lg font-semibold leading-snug">
                {certification?.chatMessage.question?.content}
              </h2>

              <Separator />

              <div className="flex items-center gap-3">
                <Avatar className="h-11 w-11">
                  <AvatarImage
                    src={
                      imageIsDefault(certification?.requestedBy?.imageUrl)
                        ? undefined
                        : certification?.requestedBy?.imageUrl
                    }
                    alt={getUserFullName(certification?.requestedBy)}
                  />
                  <AvatarFallback>
                    {getUserInitials(certification?.requestedBy)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {t("CERTIFICATIONS_DETAILS_ASKED_BY")}
                  </p>
                  <p className="truncate font-medium">
                    {getUserFullName(certification?.requestedBy)}
                  </p>
                  <p className="inline-flex items-center gap-1 truncate text-xs text-muted-foreground">
                    <Mail className="h-3 w-3 shrink-0" />
                    {certification?.requestedBy.email}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conversation */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                {t("CERTIFICATIONS_DETAILS_CONVERSATION")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ScrollToBottomContainer
                sx={{ maxHeight: "50vh", overflowY: "auto" }}
              >
                <CertificationConversation certification={certification} />
              </ScrollToBottomContainer>

              <form
                onSubmit={addMessageToCertification}
                className="flex items-end gap-2 rounded-xl border bg-background p-2"
              >
                <Textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      addMessageToCertification();
                    }
                  }}
                  placeholder={t(
                    "CERTIFICATIONS_DETAILS_SEND_MESSAGE_PLACEHOLDER"
                  )}
                  className="max-h-40 min-h-[44px] resize-none border-0 bg-transparent shadow-none focus-visible:ring-0"
                />
                <Button
                  type="submit"
                  size="icon"
                  className="shrink-0"
                  disabled={!inputMessage.trim()}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Right column: summary */}
        <div className="lg:col-span-1">
          <Card className="lg:sticky lg:top-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t("CERTIFICATIONS_DETAILS_SUMMARY") || "Résumé"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {certification && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">
                    {t("CERTIFICATIONS_DETAILS_STATUS") || "Statut"}
                  </span>
                  <StatusBadge status={certification.status} />
                </div>
              )}

              <Separator />

              {certification?.certifiedBy ? (
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage
                      src={
                        imageIsDefault(certification.certifiedBy.imageUrl)
                          ? undefined
                          : certification.certifiedBy.imageUrl
                      }
                      alt={getUserFullName(certification.certifiedBy)}
                    />
                    <AvatarFallback>
                      {getUserInitials(certification.certifiedBy)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">
                      {t("CERTIFICATIONS_ASSIGNED_EXPERT") || "Expert"}
                    </p>
                    <p className="truncate font-medium">
                      {getUserFullName(certification.certifiedBy)}
                    </p>
                    {certification.certifiedAt && (
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(certification.certifiedAt)}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t("CERTIFICATIONS_DETAILS_IN_PROGRESS") ||
                    "En attente de traitement par un expert…"}
                </p>
              )}

              {certification?.finalDecision &&
                (() => {
                  const visual = getStatusVisual(certification.status);
                  const Icon = visual.icon;
                  return (
                    <div className={cn("rounded-lg border p-3", visual.softBg)}>
                      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold">
                        <Icon className="h-3.5 w-3.5" />
                        {t(getStatusLabelKey(certification.status))}
                      </div>
                      <p className="whitespace-pre-wrap text-sm">
                        {certification.finalDecision}
                      </p>
                    </div>
                  );
                })()}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
